import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, pairwise, take } from 'rxjs/operators';
import { SelectAddressComponent } from '$core/modal/select-address/select-address.component';
import { ConfigService } from '$core/services/config.service';
import { LanguageService } from '$core/services/language.service';
import { AddressBook } from '$modules/pages/address-book/address-book';
import { AddressBookService } from '$modules/pages/address-book/address-book.service';
import { ContractAddress } from '$core/models/contract-address';

@Component({
  selector: 'app-create-delivery-location',
  templateUrl: './location-fields.component.html',
  styleUrls: ['./location-fields.component.scss'],
})
export class LocationFieldsComponent implements OnInit, OnChanges, OnDestroy {
  /*
    interface Form {
      address: FormControl<string>;
      building: FormControl<string>;
      housing: FormControl<string>;
      apartment: FormControl<string>;
      code?: FormControl<string>;
      region?: FormControl<string>;
      district?: FormControl<string>;
      locality?: {
        type: FormControl<string>;
        name: FormControl<string>;
      };
      road?: {
        type: FormControl<string>;
        name: FormControl<string>;
      };
      postcode_id?: FormControl<number>;
    }
  */
  @Input() public customAddressLabel = 'ADDRESS';
  @Input() public fieldsPrefix = '';
  @Input() public formGroup!: FormGroup;
  @Input() public queryParams = {};
  @Input() public showFullAddress = true;
  @Input() public searchUrl: string | undefined = undefined;

  @Input() public initialAddress?: Partial<AddressBook.Result>;

  @Input() public selectSavedAddressBtn = false;

  @Input() public findIndexLink = false;

  @Input() public setPostcodeId = false;

  @Input() public validateHousing = true;
  @Output() public selectAddress = new EventEmitter<
    Partial<AddressBook.Result>
  >();
  @Output() public setLoadingStatus = new EventEmitter<boolean>();

  public loadingAutocomplete = false;

  public autocompleteAddress$ = new BehaviorSubject<AddressBook.Result[]>([]);

  public autocompleteBuildings$: BehaviorSubject<string[]> =
    new BehaviorSubject([]);

  public autocompleteHousing$: BehaviorSubject<string[]> = new BehaviorSubject(
    []
  );

  private buildingMap: AddressBook.Result['building_map'] = [];

  private searchSubject = new Subject();

  public get prefix(): string {
    return this.fieldsPrefix ? this.fieldsPrefix + '-' : '';
  }

  constructor(
    private searchService: AddressBookService,
    private modalService: BsModalService,
    private configService: ConfigService,
    private languageService: LanguageService
  ) {}

  public get addressBookPath(): string | undefined {
    return this.languageService.translateRoute(
      this.configService.urlByService('addressbook')
    );
  }

  public ngOnInit(): void {
    this.formSubscriptions();
    this.searchSubscriber();

    if (this.setPostcodeId) {
      this.searchAutocomplete(this.formGroup.value.address, true);
    }

    if (this.initialAddress) {
      this.setAddress(this.initialAddress);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('queryParams' in changes) {
      const prev = changes['queryParams'].previousValue;
      const curr = changes['queryParams'].currentValue;
      if (
        prev &&
        curr['batch_mailing_recipient_address_type'] &&
        prev['batch_mailing_recipient_address_type'] !==
          curr['batch_mailing_recipient_address_type']
      ) {
        this.autocompleteAddress$.next([]);
      }
    }
    if ('initialAddress' in changes) {
      this.setAddress(this.initialAddress);
    }
  }

  public ngOnDestroy(): void {}

  public search<T>(value: { term: string; items: T[] }): void {
    this.searchSubject.next(value.term);
  }

  public customSearchFn(): boolean {
    return true;
  }

  public filterBuildings(term: string, item: any): boolean {
    return item.indexOf(term) !== -1;
  }

  public setValueOnSearch(
    value: { term: string; items: string[] },
    controlName: string,
    autocomplete$: BehaviorSubject<string[]>
  ): void {
    const control = this.formGroup.controls[controlName];

    if (autocomplete$.value.includes(value.term)) {
      control.patchValue(value.term);
    } else {
      control.patchValue(null);
    }
  }

  public setAddress(eventValues: Partial<AddressBook.Result>): void {
    if (eventValues) {
      this.buildingMap = eventValues.building_map || [];

      // number to string conversion to avoid API validation error
      const buildingsNumbers = this.buildingMap.map(({ building }) =>
        building.toString()
      );
      this.autocompleteBuildings$.next(buildingsNumbers);
    }

    this.autocompleteAddress$.next([]);

    const locationControls = this.formGroup.controls;
    const locality = locationControls.locality;
    const road = locationControls.road;
    const postcodeId = locationControls.postcode_id;
    const notNullEventValues = eventValues || {};
    const values: Partial<AddressBook.Result> = {
      id: null,
      short_address: null,
      postcode: null,
      house: null,
      building: null,
      region: null,
      district: null,
      city_type: null,
      city: null,
      street_type: null,
      street: null,

      ...notNullEventValues,

      address:
        notNullEventValues.short_address || notNullEventValues.address || null,
      flat: notNullEventValues.flat || null,
    };

    this.selectAddress.emit(values);
    locationControls.address.patchValue(values.address);
    locationControls.building.patchValue(values.house);
    locationControls.housing.patchValue(values.building);
    locationControls.apartment.patchValue(values.flat);

    if (locationControls.code) {
      locationControls.code.patchValue(values.postcode);
    }
    if (locationControls.region) {
      locationControls.region.patchValue(values.region);
    }
    if (locationControls.district) {
      locationControls.district.patchValue(values.district);
    }
    if (locationControls.city) {
      locationControls.city.patchValue(values.city);
    }
    if (locationControls.city_type) {
      locationControls.city_type.patchValue(values.city_type);
    }
    if (locationControls.street) {
      locationControls.street.patchValue(values.street);
    }
    if (locationControls.street_type) {
      locationControls.street_type.patchValue(values.street_type);
    }
    if (locationControls.cell_number) {
      locationControls.cell_number.patchValue(values.cell_number);
    }
    if (locationControls.address_type && values.address_type) {
      locationControls.address_type.patchValue(values.address_type, {
        emitEvent: false,
      });
    }
    if (locality) {
      locality.get('type').patchValue(values.city_type);
      locality.get('name').patchValue(values.city);
    }
    if (road) {
      road.get('type').patchValue(values.street_type);
      road.get('name').patchValue(values.street);
    }
    if (postcodeId) {
      postcodeId.patchValue(values.id);
    }
  }

  public selectAddressModal(): void {
    const m = this.modalService.show(SelectAddressComponent, {
      initialState: {
        selectedItem: {} as ContractAddress,
      },
      class: 'modal-lg modal-dialog-centered',
    });

    this.modalService.onHidden
      .pipe(take(1), untilDestroyed(this))
      .subscribe((res) => {
        if (res === 'saved') {
          this.setAddress(m.content.selectedItem as any);
        }
      });
  }

  private async searchAutocomplete(
    search: string,
    setId?: boolean
  ): Promise<void> {
    this.loadingAutocomplete = true;
    this.setLoadingStatus.emit(this.loadingAutocomplete);
    try {
      const autocompleteAddress = await this.searchService
        .getAutocomplete(
          {
            search,
            ...this.queryParams,
          },
          this.searchUrl
        )
        .toPromise();
      if (setId) {
        this.formGroup.get('postcode_id').setValue(autocompleteAddress[0].id);
      }
      this.autocompleteAddress$.next(autocompleteAddress);
      this.selectAddress.emit(autocompleteAddress[0]);
    } catch (error) {
    } finally {
      this.loadingAutocomplete = false;
      this.setLoadingStatus.emit(this.loadingAutocomplete);
    }
  }

  private searchSubscriber(): void {
    this.searchSubject
      .pipe(
        untilDestroyed(this),

        debounceTime(600)
      )
      .subscribe((res: string) => {
        if (res) {
          this.searchAutocomplete(res);
        } else {
          this.autocompleteAddress$.next([]);
        }
      });
  }

  private onBuildingChange = (): void => {
    const locationControls = this.formGroup.controls;
    const buildingMapItem = this.buildingMap.find(
      ({ building }) => building.toString() === locationControls.building.value
    );

    let isHousingRequired = false;
    let isNoHousing = false;

    if (buildingMapItem && buildingMapItem.housings.length) {
      const housingsWithoutNull = buildingMapItem.housings.filter(
        (item) => item !== null
      );

      this.autocompleteHousing$.next(housingsWithoutNull);
      isHousingRequired = buildingMapItem.housings[0] !== null;
      isNoHousing = !isHousingRequired && buildingMapItem.housings.length === 1;
    } else {
      this.autocompleteHousing$.next([]);
    }

    if (isNoHousing) {
      locationControls.housing.disable();
    } else {
      locationControls.housing.enable();
    }

    if (isHousingRequired && this.validateHousing) {
      locationControls.housing.setValidators([Validators.required]);
    } else {
      locationControls.housing.setValidators([]);
    }

    locationControls.housing.reset(null);
    locationControls.housing.updateValueAndValidity({ onlySelf: true });
  };

  private isValuesChanged<T>([prevValue, curValue]: [T, T]): boolean {
    return prevValue !== curValue;
  }

  private formSubscriptions(): void {
    const locationControls = this.formGroup.controls;

    locationControls.address.valueChanges
      .pipe(
        pairwise(),

        filter(this.isValuesChanged),

        untilDestroyed(this)
      )
      .subscribe(() => {
        locationControls.building.reset(null);

        if (!locationControls.address.value) {
          this.autocompleteBuildings$.next([]);
        }
      });

    locationControls.building.valueChanges
      .pipe(
        pairwise(),

        filter(this.isValuesChanged),

        untilDestroyed(this)
      )
      .subscribe(this.onBuildingChange);

    locationControls.housing.valueChanges
      .pipe(
        pairwise(),

        filter(this.isValuesChanged),

        untilDestroyed(this)
      )
      .subscribe(() => {
        locationControls.apartment.reset('');
      });
  }
}
